import { isNullable } from "@krainovsd/js-helpers";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import type { ComposerTranslation } from "vue-i18n";
import { INDICATORS_BY_KEY, INDICATOR_OPTIONS, REPORT_STRING_OPTIONS } from "./report.constants";
import { DEFAULT_FILTERS } from "./report.store";
import type {
  IndicatorComparison,
  IndicatorFormat,
  IndicatorType,
  PeriodType,
  ReportFilters,
  ReportResponse,
  ReportResponseRows,
  ReportRow,
  ReportUpdateOrCreateBody,
  RequestStatisticsBody,
  SavedFilter,
  SavedFilterField,
  StatisticsListFilters,
  ViewType,
} from "./report.types";

dayjs.extend(isoWeek);

const PERIOD_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

/** Periods that select a window of complete days ending yesterday. */
const PERIOD_LAST_DAYS: Record<number, number> = {
  11: 1, // вчера (предыдущие календарные сутки)
  10: 3, // последние 3 дня
  1: 7, // последние 7 дней
  2: 30, // последние 30 дней
  3: 90, // последние 90 дней
  4: 365, // последние 365 дней
};

/**
 * Resolve a period preset (see PeriodType) into a concrete date range,
 * formatted as local time strings (e.g. `2026-04-16 09:28:56`).
 *
 * Period 0 ("Последние сутки") is a rolling 24-hour window ending now. Period 11
 * ("Вчера") is the previous whole calendar day. Presets 1-4 are rolling windows
 * of complete days ending yesterday. Presets 5-9 are the current
 * day/week/month/quarter/year, running up to the end of today.
 */
export function getPeriodRange(period: number): { dateFrom: string; dateTo: string } {
  const now = dayjs();

  // Period 0 — a rolling 24-hour window ending now (not the previous calendar day).
  if (period === 0) {
    return {
      dateFrom: now.subtract(24, "hour").format(PERIOD_DATE_FORMAT),
      dateTo: now.format(PERIOD_DATE_FORMAT),
    };
  }

  // Periods 5–9 aren't in `PERIOD_LAST_DAYS`, so `lastDays` is `undefined`
  // there — and `undefined !== null` is `true`, which used to fall through to
  // the rolling-window branch and call `subtract(undefined, "day")`, producing
  // an Invalid Date for "today" and friends. Test against `undefined` instead.
  const lastDays = PERIOD_LAST_DAYS[period];
  if (lastDays !== undefined) {
    return {
      dateFrom: now.subtract(lastDays, "day").startOf("day").format(PERIOD_DATE_FORMAT),
      dateTo: now.subtract(1, "day").endOf("day").format(PERIOD_DATE_FORMAT),
    };
  }

  let from = now.startOf("day");
  switch (period) {
    case 6: // эта неделя
      from = now.startOf("isoWeek");
      break;
    case 7: // этот месяц
      from = now.startOf("month");
      break;
    case 8: // квартал
      from = now.month(Math.floor(now.month() / 3) * 3).startOf("month");
      break;
    case 9: // этот год
      from = now.startOf("year");
      break;
    case 5: // сегодня
    default:
      from = now.startOf("day");
  }

  return {
    dateFrom: from.format(PERIOD_DATE_FORMAT),
    dateTo: now.endOf("day").format(PERIOD_DATE_FORMAT),
  };
}

/**
 * Build a fixed period range from two calendar dates, formatted as local
 * time strings (see {@link getPeriodRange}). The range covers whole days:
 * `from` at the start of its day, `to` at the end of its day.
 */
export function getCustomPeriodRange(from: Date, to: Date): { dateFrom: string; dateTo: string } {
  return {
    dateFrom: dayjs(from).startOf("day").format(PERIOD_DATE_FORMAT),
    dateTo: dayjs(to).endOf("day").format(PERIOD_DATE_FORMAT),
  };
}

/**
 * Display format for a date section's grouping cell: months collapse to
 * "MM.YYYY" (e.g. `01.2026`); every other date section (days/weeks/day-of-week)
 * keeps the full "DD.MM.YYYY". Shared by the report and factor-comparison tables.
 */
export function dateFormatForSection(section: number | null): string {
  return section === REPORT_STRING_OPTIONS.month ? "MM.YYYY" : "DD.MM.YYYY";
}

// The calendar span each date section's rows cover, so drilling into one cell
// can scope the report to exactly that span. `daysOfWeek` aggregates across
// weeks (a row isn't a single calendar date), so it has no resolvable range.
const DATE_SECTION_SPAN: Partial<Record<number, "day" | "isoWeek" | "month">> = {
  [REPORT_STRING_OPTIONS.days]: "day",
  [REPORT_STRING_OPTIONS.weeks]: "isoWeek",
  [REPORT_STRING_OPTIONS.month]: "month",
};

/**
 * Build a fixed period range from a single date-section cell value (the row's
 * `date`), covering the whole day/week/month that the cell represents. Used when
 * drilling into a date row so `filters.period` narrows to that span. Returns
 * `null` for unparseable values or sections without a calendar span (e.g.
 * day-of-week aggregates).
 */
export function dateCellToFixedPeriod(
  value: unknown,
  section: number | null,
): { dateFrom: string; dateTo: string } | null {
  if (section === null) return null;
  const span = DATE_SECTION_SPAN[section];
  if (!span) return null;
  const parsed = dayjs(value as string | number | Date);
  if (!parsed.isValid()) return null;
  return {
    dateFrom: parsed.startOf(span).format(PERIOD_DATE_FORMAT),
    dateTo: parsed.endOf(span).format(PERIOD_DATE_FORMAT),
  };
}

/**
 * Given a concrete date range (local time strings, see {@link getPeriodRange}),
 * return the immediately preceding range of the same length. The previous range
 * ends one second before `dateFrom` and spans the same duration, so a 7-day
 * window `[20.05 00:00:00 .. 26.05 23:59:59]` maps to `[13.05 00:00:00 .. 19.05 23:59:59]`.
 */
export function getPreviousPeriodRange(
  dateFrom: string,
  dateTo: string,
): { dateFrom: string; dateTo: string } {
  const from = dayjs(dateFrom);
  const to = dayjs(dateTo);
  const span = to.diff(from);
  const previousTo = from.subtract(1, "second");
  const previousFrom = previousTo.subtract(span, "millisecond");

  return {
    dateFrom: previousFrom.format(PERIOD_DATE_FORMAT),
    dateTo: previousTo.format(PERIOD_DATE_FORMAT),
  };
}

/**
 * Build a copy of a statistics request body shifted to the previous period of
 * equal length. A period preset is first resolved to concrete dates (via
 * {@link getPeriodRange}); the preset is then dropped so `fetchReport` keeps the
 * explicit previous range. Returns `null` when the body has no resolvable period.
 */
export function buildPreviousPeriodBody(body: RequestStatisticsBody): RequestStatisticsBody | null {
  const current = !isNullable(body.period)
    ? getPeriodRange(body.period)
    : body.dateFrom && body.dateTo
      ? { dateFrom: body.dateFrom, dateTo: body.dateTo }
      : null;
  if (!current) return null;

  const previous = getPreviousPeriodRange(current.dateFrom, current.dateTo);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { period: _period, ...rest } = body;

  return { ...rest, ...previous };
}

// Indicator values show at most two fraction digits (integers stay clean, since
// the minimum defaults to 0), e.g. 1234.5 → "1 234,5", 1234.567 → "1 234,57".
const numberFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

// Set of all known indicator field names, used to skip non-indicator row fields
// (id, date, name, ...) when aggregating.
const INDICATOR_NAMES = new Set<string>(INDICATOR_OPTIONS.map((option) => option.name));

/**
 * Coerce an indicator value to a number. "number"-format indicators arrive as
 * numbers; "money"/"percent" arrive as numeric strings (e.g. "4.53", "10 000 ₽"),
 * so we strip everything but digits, sign and decimal separator before parsing.
 */
export function parseIndicatorValue(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const normalized = value
      .replace(/\s/g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, "");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

/**
 * Format an indicator value for display by its declared format: "percent" → "12%",
 * "money" → "12 000 ₽" (symbol from state), anything else → a plain grouped number.
 */
export function formatIndicatorValue(
  value: unknown,
  format: IndicatorFormat | undefined,
  currencySymbol?: string,
): string {
  const number = numberFormatter.format(parseIndicatorValue(value));
  switch (format) {
    case "percent":
      return `${number}%`;
    case "money":
      return `${number} ${currencySymbol ?? "₽"}`;
    default:
      return number;
  }
}

type IndicatorAggregate = { sum: number; count: number };

// Walk the report's top-level (grouping) rows and accumulate, per indicator,
// the running sum and row count. Values may be numbers or numeric strings (see
// parseIndicatorValue); non-indicator fields are skipped.
function accumulateIndicators(response: ReportResponse): Map<string, IndicatorAggregate> {
  const acc = new Map<string, IndicatorAggregate>();
  for (const { grouping } of response.statistics ?? []) {
    for (const row of grouping ?? []) {
      for (const [field, value] of Object.entries(row)) {
        if (!INDICATOR_NAMES.has(field)) continue;
        const parsed = parseIndicatorValue(value);
        const existing = acc.get(field);
        if (existing) {
          existing.sum += parsed;
          existing.count += 1;
        } else {
          acc.set(field, { sum: parsed, count: 1 });
        }
      }
    }
  }

  return acc;
}

// Aggregate each indicator according to its declared `calculation`, mirroring
// the React `getCalculation` in apps/afina-react's statistic-downbar:
//   - "sum"  → straight sum of parsed values
//   - "mean" → arithmetic mean (sum/count)
//   - "arpc" → sum(approvedSum) / sum(sends) (0 when sends is 0)
function aggregateReportIndicators(response: ReportResponse): Record<string, number> {
  const acc = accumulateIndicators(response);
  const result: Record<string, number> = {};

  for (const option of INDICATOR_OPTIONS) {
    const entry = acc.get(option.name);
    if (!entry) continue;

    switch (option.calculation) {
      case "mean":
        result[option.name] = entry.count > 0 ? entry.sum / entry.count : 0;
        break;
      // case "arpc": {
      //   const sends = acc.get("sends")?.sum ?? 0;
      //   const approvedSum = acc.get("approvedSum")?.sum ?? 0;
      //   result[option.name] = sends > 0 ? approvedSum / sends : 0;
      //   break;
      // }
      case "sum":
      default:
        result[option.name] = entry.sum;
    }
  }

  return result;
}

// Percent change of the current period against the previous one, rounded to two
// decimals. When the previous total is zero we avoid dividing by zero: 0 if the
// current total is also zero, otherwise 100 (treated as a full increase).
function getDiffPercent(firstValue: number, secondValue: number): number {
  if (secondValue === 0) return firstValue === 0 ? 0 : 100;
  return Math.round(((firstValue - secondValue) / secondValue) * 10000) / 100;
}

/**
 * Compare aggregated indicators of the current period (`first`) against the
 * previous period (`second`). Returns, per indicator field, both totals and the
 * percent difference, e.g. `{ sends: { firstValue: 233, secondValue: 400, diffPercent: -41.75 } }`.
 */
export function compareReportIndicators(
  first: ReportResponse,
  second: ReportResponse,
): Record<string, IndicatorComparison> {
  const firstTotals = aggregateReportIndicators(first);
  const secondTotals = aggregateReportIndicators(second);
  const fields = new Set([...Object.keys(firstTotals), ...Object.keys(secondTotals)]);

  const result: Record<string, IndicatorComparison> = {};
  for (const field of fields) {
    const firstValue = firstTotals[field] ?? 0;
    const secondValue = secondTotals[field] ?? 0;
    result[field] = {
      firstValue,
      secondValue,
      diffPercent: getDiffPercent(firstValue, secondValue),
    };
  }

  return result;
}

/**
 * Whether a "Подстроки" (groupByNested) option must be disabled given the
 * selected "Строки" (section), mirroring isDisabled() in the React report form:
 * a date row disables itself and every coarser date row, and the triggers row
 * disables offers/channels/creatives.
 */
export function isReportSubstringDisabled(section: number | null, value: number): boolean {
  const o = REPORT_STRING_OPTIONS;
  switch (section) {
    case o.days:
      return value === o.days || value === o.daysOfWeek || value === o.weeks || value === o.month;
    case o.daysOfWeek:
      return value === o.daysOfWeek || value === o.weeks || value === o.month;
    case o.weeks:
      return value === o.weeks || value === o.month;
    case o.month:
      return value === o.month;
    case o.triggers:
      return value === o.offers || value === o.channels || value === o.creatives;
    default:
      return false;
  }
}

export function getIndicators(value: number[], t: ComposerTranslation) {
  return value.map((key) => {
    const option = INDICATORS_BY_KEY[key];
    return {
      value: key,
      label: option ? t(option.title.path, {}, { default: option.title.default }) : String(key),
    };
  });
}

export function responseRowsToReportRows(rows: ReportResponseRows[]): ReportRow[] {
  return rows.flatMap(({ grouping, splitting }) => {
    const children = splitting ?? [];
    return (grouping ?? []).map((row) => ({
      ...row,
      ...(children.length > 0 ? { children } : {}),
    }));
  });
}

export function savedFilterToReportFilters(body: SavedFilter): ReportFilters {
  const hasDateRange = body.dateFrom !== null && body.dateTo !== null;
  return {
    ...DEFAULT_FILTERS,
    attribution: body.view ?? null,
    section: body.grouping ?? null,
    groupByNested: body.splitting ?? null,
    period: body.period ?? (hasDateRange ? "fixPeriod" : null),
    dateFrom: body.dateFrom,
    dateTo: body.dateTo,
    currency: body.currency,
    indicators: body.indicators,
    providers: (body.filters?.trafficProviders ?? []).map((item) => item.id),
    offers: (body.filters?.offers?.list_ids ?? []).map((item) => item.id),
    creatives: (body.filters?.creatives?.list_ids ?? []).map((item) => item.id),
    campaigns: (body.filters?.campaigns ?? []).map((item) => item.id),
    mailingTypes: (body.filters?.mailingTypes ?? []).map((item) => item.id),
    triggers: [],
  };
}

function savedListToIds(list: SavedFilterField[] | null | undefined): number[] | undefined {
  return list?.length ? list.map((item) => item.id) : undefined;
}

// Convert the saved filter's nested {id,name} lists back into the id-only shape
// the create/update request expects.
function savedFiltersToRequestFilters(
  saved: SavedFilter["filters"] | undefined,
): StatisticsListFilters {
  if (!saved) return {};
  return {
    offers: {
      list_ids: savedListToIds(saved.offers?.list_ids),
      group: savedListToIds(saved.offers?.group),
      vertical: savedListToIds(saved.offers?.vertical),
    },
    creatives: {
      list_ids: savedListToIds(saved.creatives?.list_ids),
      group: savedListToIds(saved.creatives?.group),
      vertical: savedListToIds(saved.creatives?.vertical),
      type: saved.creatives?.type ?? undefined,
    },
    campaigns: savedListToIds(saved.campaigns),
    channels: savedListToIds(saved.channels),
    mailingTypes: savedListToIds(saved.mailingTypes),
  };
}

/**
 * Merge the saved report response with the current front-end filters into the
 * body for POST /report/create or PUT /report/update/{id}. The front filters win
 * for the dimensions they own (period, grouping, indicators, providers, …); every
 * other field (type, hash, sorting, the remaining filter dimensions) is carried
 * over from the saved response, which is `null` for a brand-new report.
 */
export function buildReportBody(
  filters: ReportFilters,
  name: string,
  saved: SavedFilter | null,
): ReportUpdateOrCreateBody {
  const isFixed = filters.period === "fixPeriod";
  const period = typeof filters.period === "number" ? (filters.period as PeriodType) : undefined;

  return {
    type: saved?.type ?? 1,
    name,
    view: (filters.attribution ?? saved?.view ?? 0) as ViewType,
    grouping: filters.section ?? saved?.grouping ?? 0,
    splitting: filters.groupByNested ?? undefined,
    indicators: [...filters.indicators] as IndicatorType[],
    currencyId: filters.currency?.id ?? saved?.currency?.id ?? 0,
    period,
    dateFrom: isFixed ? (filters.dateFrom ?? undefined) : undefined,
    dateTo: isFixed ? (filters.dateTo ?? undefined) : undefined,
    filters: (() => {
      const base = savedFiltersToRequestFilters(saved?.filters);
      // Front filters own list_ids / flat arrays; saved values for the other
      // sub-fields (group, vertical, type, channels) survive the merge.
      return {
        ...base,
        offers: {
          ...base.offers,
          list_ids: filters.offers.length ? [...filters.offers] : base.offers?.list_ids,
        },
        creatives: {
          ...base.creatives,
          list_ids: filters.creatives.length ? [...filters.creatives] : base.creatives?.list_ids,
        },
        campaigns: filters.campaigns.length ? [...filters.campaigns] : base.campaigns,
        mailingTypes: filters.mailingTypes.length ? [...filters.mailingTypes] : base.mailingTypes,
        trafficProviders: filters.providers.length ? [...filters.providers] : base.trafficProviders,
        triggers: filters.triggers.length ? [...filters.triggers] : undefined,
      };
    })(),
    exceptions: saved?.exceptions ?? undefined,
    sorting: saved?.sorting,
    hash: saved?.hash,
  };
}
